import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { adminApi } from "../../api/admin";
import type { AdminPromoCode, AdminPromoRedemption } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import { formatCredits, formatDateTime, utcTooltip } from "../../shared/format";
import { roleAtLeast, type StaffRole } from "../../shared/hooks/useStaff";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Input,
  Modal,
  Spinner,
} from "../../ui";
import { StaffAction, stateTone } from "./shared";

type CodeStatus = "active" | "expired" | "used up";

function statusOf(code: AdminPromoCode): CodeStatus {
  if (code.expires_at && new Date(code.expires_at).getTime() <= Date.now())
    return "expired";
  if (code.max_uses !== null && code.use_count >= code.max_uses)
    return "used up";
  return "active";
}

function statusTone(status: CodeStatus) {
  if (status === "active") return "success" as const;
  return status === "expired" ? ("danger" as const) : ("warning" as const);
}

/** An ISO time as the value a `datetime-local` input shows, in the viewer's zone. */
function toLocalInput(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

/** Blank means no limit; anything else must be a whole number above zero. */
function parseMaxUses(value: string): number | null | "invalid" {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : "invalid";
}

const redemptionColumns = (
  product: string,
): ColumnDef<AdminPromoRedemption, unknown>[] => [
  {
    id: "user",
    header: "User",
    cell: ({ row }) => (
      <Link
        className="text-brand underline"
        to={`/admin/p/${product}/users/${row.original.user_id}`}
      >
        {row.original.email}
      </Link>
    ),
  },
  { accessorKey: "name", header: "Name" },
  {
    id: "credits",
    header: "Credits",
    cell: ({ row }) => formatCredits(row.original.credits),
  },
  {
    id: "state",
    header: "State",
    cell: ({ row }) => (
      <Badge tone={stateTone(row.original.state)}>
        {row.original.state === "fulfilled" ? "collected" : "waiting"}
      </Badge>
    ),
  },
  {
    id: "created_at",
    header: "Added",
    cell: ({ row }) => (
      <span title={utcTooltip(row.original.created_at)}>
        {formatDateTime(row.original.created_at)}
      </span>
    ),
  },
  {
    id: "fulfilled_at",
    header: "Collected",
    cell: ({ row }) => (
      <span title={utcTooltip(row.original.fulfilled_at)}>
        {formatDateTime(row.original.fulfilled_at)}
      </span>
    ),
  },
];

function Redemptions({
  product,
  code,
  onClose,
}: {
  product: string;
  code: AdminPromoCode;
  onClose: () => void;
}) {
  const query = useQuery({
    queryKey: ["admin", "promo-redemptions", code.id],
    queryFn: () => adminApi.promoRedemptions(product, code.id),
  });
  return (
    <section className="mt-6" aria-label={`Redemptions of ${code.code}`}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Redemptions of {code.code}</h2>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
      {query.isLoading ? <Spinner /> : null}
      {query.isError ? (
        <Alert kind="error">{errorMessage(query.error)}</Alert>
      ) : null}
      {query.data ? (
        <DataTable<AdminPromoRedemption>
          columns={redemptionColumns(product)}
          data={query.data.items}
          emptyMessage="Nobody has added this code yet."
        />
      ) : null}
    </section>
  );
}

const EMPTY_CODE = {
  code: "",
  credits: "",
  maxUses: "",
  expires: "",
};

export function PromoCodesPage() {
  const { role, product } = useOutletContext<{
    role: StaffRole;
    product: string | null;
  }>();
  const slug = product ?? "";
  const canMutate = roleAtLeast(role, "admin");
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const [creating, setCreating] = useState<typeof EMPTY_CODE | null>(null);
  const [editing, setEditing] = useState<{
    code: AdminPromoCode;
    maxUses: string;
    expires: string;
  } | null>(null);
  const [expiring, setExpiring] = useState<AdminPromoCode | null>(null);
  const [viewing, setViewing] = useState<AdminPromoCode | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["admin", "promo-codes", slug],
    enabled: slug !== "",
    queryFn: () => adminApi.promoCodes(slug),
  });

  const done = (text: string) => {
    setMessage({ kind: "success", text });
    setCreating(null);
    setEditing(null);
    setExpiring(null);
    setFormError(null);
    return queryClient.invalidateQueries({
      queryKey: ["admin", "promo-codes"],
    });
  };

  const create = () => {
    if (!creating) return;
    const credits = Number(creating.credits);
    const maxUses = parseMaxUses(creating.maxUses);
    if (!/^[A-Za-z0-9_-]{3,64}$/.test(creating.code))
      return setFormError(
        "A code is 3 to 64 letters, digits, dashes or underscores.",
      );
    if (!Number.isInteger(credits) || credits <= 0)
      return setFormError("Credits must be a whole number above zero.");
    if (maxUses === "invalid")
      return setFormError("Use limit must be a whole number above zero.");
    adminApi
      .createPromoCode(slug, {
        code: creating.code,
        credits,
        max_uses: maxUses,
        expires_at: fromLocalInput(creating.expires),
      })
      .then((created) => done(`Code ${created.code} created.`))
      .catch((err) => setFormError(errorMessage(err)));
  };

  const save = () => {
    if (!editing) return;
    const maxUses = parseMaxUses(editing.maxUses);
    if (maxUses === "invalid")
      return setFormError("Use limit must be a whole number above zero.");
    adminApi
      .updatePromoCode(slug, editing.code.id, {
        max_uses: maxUses,
        expires_at: fromLocalInput(editing.expires),
      })
      .then((updated) => done(`Code ${updated.code} updated.`))
      .catch((err) => setFormError(errorMessage(err)));
  };

  const expire = (code: AdminPromoCode) =>
    adminApi
      .expirePromoCode(slug, code.id)
      .then(() => done(`Code ${code.code} expired.`))
      .catch((err) => {
        setMessage({ kind: "error", text: errorMessage(err) });
        setExpiring(null);
      });

  const columns: ColumnDef<AdminPromoCode, unknown>[] = [
    {
      id: "code",
      header: "Code",
      cell: ({ row }) => (
        <span className="font-mono font-semibold">{row.original.code}</span>
      ),
    },
    {
      id: "credits",
      header: "Credits",
      cell: ({ row }) => formatCredits(row.original.credits),
    },
    {
      id: "used",
      header: "Collected",
      cell: ({ row }) => (
        <span>
          {row.original.use_count} of {row.original.max_uses ?? "no limit"}
          {row.original.pending_count > 0 ? (
            <span className="block text-xs text-slate-500">
              {row.original.pending_count} waiting to collect
            </span>
          ) : null}
        </span>
      ),
    },
    {
      id: "expires_at",
      header: "Expires",
      cell: ({ row }) =>
        row.original.expires_at ? (
          <span title={utcTooltip(row.original.expires_at)}>
            {formatDateTime(row.original.expires_at)}
          </span>
        ) : (
          "never"
        ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = statusOf(row.original);
        return <Badge tone={statusTone(status)}>{status}</Badge>;
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            aria-label={`Redemptions of ${row.original.code}`}
            onClick={() => setViewing(row.original)}
          >
            Redemptions
          </Button>
          <StaffAction
            allowed={canMutate}
            onClick={() => {
              setFormError(null);
              setEditing({
                code: row.original,
                maxUses: row.original.max_uses?.toString() ?? "",
                expires: toLocalInput(row.original.expires_at),
              });
            }}
          >
            Edit limits
          </StaffAction>
          {statusOf(row.original) === "expired" ? null : (
            <StaffAction
              allowed={canMutate}
              variant="danger"
              onClick={() => setExpiring(row.original)}
            >
              Expire now
            </StaffAction>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Promo codes</h1>
        <StaffAction
          allowed={canMutate}
          variant="primary"
          onClick={() => {
            setFormError(null);
            setCreating(EMPTY_CODE);
          }}
        >
          New code
        </StaffAction>
      </div>
      {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
      {query.isLoading ? <Spinner /> : null}
      {query.isError ? (
        <Alert kind="error">{errorMessage(query.error)}</Alert>
      ) : null}
      {query.data ? (
        <DataTable<AdminPromoCode>
          columns={columns}
          data={query.data.items}
          emptyMessage="No promo codes yet."
        />
      ) : null}
      {viewing ? (
        <Redemptions
          key={viewing.id}
          product={slug}
          code={viewing}
          onClose={() => setViewing(null)}
        />
      ) : null}

      <Modal
        open={creating !== null}
        title="New promo code"
        onClose={() => setCreating(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreating(null)}>
              Cancel
            </Button>
            <Button onClick={create}>Create</Button>
          </>
        }
      >
        {creating ? (
          <div className="flex flex-col gap-3">
            <Input
              label="Code"
              value={creating.code}
              onChange={(e) =>
                setCreating({
                  ...creating,
                  code: e.target.value.toUpperCase(),
                })
              }
            />
            <Input
              label="Credits"
              inputMode="numeric"
              value={creating.credits}
              onChange={(e) =>
                setCreating({ ...creating, credits: e.target.value })
              }
            />
            <Input
              label="Use limit (blank for none)"
              inputMode="numeric"
              value={creating.maxUses}
              onChange={(e) =>
                setCreating({ ...creating, maxUses: e.target.value })
              }
            />
            <Input
              label="Expires (blank for never)"
              type="datetime-local"
              value={creating.expires}
              onChange={(e) =>
                setCreating({ ...creating, expires: e.target.value })
              }
            />
            <p className="text-xs text-slate-500">
              A user adds the code to their account and collects the credits
              once their email is verified and a broker is connected. The code
              and its credits cannot be changed afterwards.
            </p>
            {formError ? <Alert kind="error">{formError}</Alert> : null}
          </div>
        ) : null}
      </Modal>

      <Modal
        open={editing !== null}
        title={`Limits of ${editing?.code.code ?? "code"}`}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save}>Save</Button>
          </>
        }
      >
        {editing ? (
          <div className="flex flex-col gap-3">
            <p>
              Collected {editing.code.use_count}{" "}
              {editing.code.use_count === 1 ? "time" : "times"} so far. The
              limit counts payouts: at or below that number, nobody else can
              collect.
            </p>
            <Input
              label="Use limit (blank for none)"
              inputMode="numeric"
              value={editing.maxUses}
              onChange={(e) =>
                setEditing({ ...editing, maxUses: e.target.value })
              }
            />
            <Input
              label="Expires (blank for never)"
              type="datetime-local"
              value={editing.expires}
              onChange={(e) =>
                setEditing({ ...editing, expires: e.target.value })
              }
            />
            {formError ? <Alert kind="error">{formError}</Alert> : null}
          </div>
        ) : null}
      </Modal>

      <Modal
        open={expiring !== null}
        title={`Expire ${expiring?.code ?? "code"} now?`}
        onClose={() => setExpiring(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setExpiring(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => expiring && void expire(expiring)}
            >
              Confirm
            </Button>
          </>
        }
      >
        Nobody can add or collect it from this moment, including people who
        added it earlier. Clearing the expiry under Edit limits reopens it.
      </Modal>
    </Card>
  );
}
